/**
 * -------
 * threeVR (https://github.com/richtr/threeVR)
 * -------
 *
 * W3C Device Orientation control (http://www.w3.org/TR/orientation-event/)
 * with manual user drag (rotate) and pinch (zoom) override handling
 *
 * Author: Rich Tibbett (http://github.com/richtr)
 * License: The MIT License
 *
**/

import * as THREE from 'three'

type ThreeEventData = any[] & {
	type?: string,
	[attachment: string]: any,
}

enum CONTROLLER_EVENT {
	CALIBRATE_COMPASS = 'compassneedscalibration',
	SCREEN_ORIENTATION = 'orientationchange',
	MANUAL_CONTROL = 'userinteraction', // userinteractionstart, userinteractionend
	ZOOM_CONTROL = 'zoom', // zoomstart, zoomend
	ROTATE_CONTROL = 'rotate', // rotatestart, rotateend
}

enum CONTROLLER_STATE {
	AUTO = 0,
	MANUAL_ROTATE = 1,
	MANUAL_ZOOM = 2,
}

export enum MOVE_CAMERA_ON_OPTIONS {
	LEFT_MOUSE_BUTTON = 0,
	MIDDLE_MOUSE_BUTTON = 1,
	RIGHT_MOUSE_BUTTON = 2,
	ANY = 3,
}

export class DeviceOrientationController extends THREE.EventDispatcher {
	constructor (
		public object: THREE.PerspectiveCamera,
		public element: HTMLElement = document.body,
		options?: {
			freeze?: boolean,
			enableManualDrag?: boolean,
			enableManualZoom?: boolean,
			useQuaternions?: boolean,
		},
	) {
		super()
		this.enableManualDrag = options?.enableManualDrag ?? true // enable manual user drag override control by default
		this.enableManualZoom = options?.enableManualZoom ?? true // enable manual user zoom override control by default
		this.useQuaternions = options?.useQuaternions ?? true // use quaternions for orientation calculation by default
		this.freeze = options?.freeze ?? true

		this.startFOVFrustrumHeight = 2000 * Math.tan( THREE.Math.degToRad( ( this.object.fov || 75 ) / 2 ) )
	}

	freeze: boolean
	enableManualDrag: boolean
	enableManualZoom: boolean
	useQuaternions: boolean
	deviceOrientation: Partial<DeviceOrientationEvent> = {}
	screenOrientation = window.orientation || 0

	// Manual rotate override components
	startX = 0
	startY = 0
	currentX = 0
	currentY = 0
	tmpQuat = new THREE.Quaternion()

	// Manual zoom override components
	zoomStart = 1
	zoomCurrent = 1
	zoomP1 = new THREE.Vector2()
	zoomP2 = new THREE.Vector2()
	tmpFOV: number

	CONTROLLER_STATE = CONTROLLER_STATE

	CONTROLLER_EVENT = CONTROLLER_EVENT

	appState: CONTROLLER_STATE = CONTROLLER_STATE.AUTO

	// Consistent Object Field-Of-View fix components
	startClientHeight = window.innerHeight
	startFOVFrustrumHeight: number
	relativeFOVFrustrumHeight: number
	relativeVerticalFOV: number
	deviceQuat = new THREE.Quaternion()

	private _moveCameraOn = MOVE_CAMERA_ON_OPTIONS.RIGHT_MOUSE_BUTTON

	public get moveCameraOn() {
		return this._moveCameraOn
	}

	public set moveCameraOn(newValue: MOVE_CAMERA_ON_OPTIONS) {
		this._moveCameraOn = newValue
	}

	static maximalFov = 100
	static minimumFov = 20

	fireEvent = (...args: ThreeEventData) => {
		return (name: string) => {
			let eventData = args || [] as ThreeEventData

			eventData.type = name;
			eventData.target = this;

			this.dispatchEvent( eventData as RequiredKey<ThreeEventData, "type"> );
		}
	}

	constrainObjectFOV = () => {
		this.relativeFOVFrustrumHeight = this.startFOVFrustrumHeight * ( window.innerHeight / this.startClientHeight );

		this.relativeVerticalFOV = THREE.Math.radToDeg( 2 * Math.atan( this.relativeFOVFrustrumHeight / 2000 ) );
		this.object.fov = this.relativeVerticalFOV;
	}

	onDeviceOrientationChange = ( event: DeviceOrientationEvent ) => {
		return
		this.deviceOrientation = event;
	}

	onScreenOrientationChange = () => {
		this.screenOrientation = window.orientation || 0;

		this.fireEvent( this.CONTROLLER_EVENT.SCREEN_ORIENTATION );
	}

	onCompassNeedsCalibration = ( event: Event ) => {
		event.preventDefault();
		return
		this.fireEvent( this.CONTROLLER_EVENT.CALIBRATE_COMPASS );
	}

	private _startMovingCamera = (event: MouseEvent | Touch) => {
		if (this.enableManualDrag) {
			if (event instanceof MouseEvent) {
				event.preventDefault()
			}

			this.appState = this.CONTROLLER_STATE.MANUAL_ROTATE

			this.freeze = true

			this.tmpQuat.copy(this.object.quaternion)

			this.startX = this.currentX = event.pageX
			this.startY = this.currentY = event.pageY

			if (event instanceof MouseEvent) {
				this.element.addEventListener('mousemove', this.onDocumentMouseMove, false)
				this.element.addEventListener('mouseup', this.onDocumentMouseUp, { passive: true })
			} else {
				this.element.addEventListener('touchmove', this.onDocumentTouchMove, false)
				this.element.addEventListener('touchend', this.onDocumentTouchEnd, false)
			}

			this.fireEvent(this.CONTROLLER_EVENT.MANUAL_CONTROL + 'start')
			this.fireEvent(this.CONTROLLER_EVENT.ROTATE_CONTROL + 'start')
		}
	}

	private _moveCamera = (event: MouseEvent | Touch) => {
		this.currentX = event.pageX;
		this.currentY = event.pageY;
	}

	private _stopMovingCamera = () => {
		this.appState = this.CONTROLLER_STATE.AUTO; // reset control state

		this.freeze = false;

		this.fireEvent( this.CONTROLLER_EVENT.MANUAL_CONTROL + 'end' );
		this.fireEvent( this.CONTROLLER_EVENT.ROTATE_CONTROL + 'end' );
	}

	onDocumentMouseDown = ( event: MouseEvent ) => {
		this._startMovingCamera(event)
	}

	onDocumentMouseMove = ( event: MouseEvent ) => {
		// MouseEvent.buttons is a number representing binary flags for each button that can be pressed. These flags can
		// be extracted using the bitwise and operation. If the result is truthy, the required flag is set.
		const extractionFlags = {
			[MOVE_CAMERA_ON_OPTIONS.LEFT_MOUSE_BUTTON]: 0b001,
			[MOVE_CAMERA_ON_OPTIONS.RIGHT_MOUSE_BUTTON]: 0b010,
			[MOVE_CAMERA_ON_OPTIONS.MIDDLE_MOUSE_BUTTON]: 0b100,
			[MOVE_CAMERA_ON_OPTIONS.ANY]: 0b111,
		}
		const extractFlagForCameraMoveButton = extractionFlags[this.moveCameraOn]

		if (event.buttons & extractFlagForCameraMoveButton) {
			this._moveCamera(event)
		}
	}

	onDocumentContextMenu = (event: MouseEvent) => {
		if (this.moveCameraOn >= 2) {
			// canvas is going to be dragged, so prevend context menu from popping up
			event.preventDefault()
		}
	}

	onDocumentMouseUp = () => {
		this.element.removeEventListener('mousemove', this.onDocumentMouseMove, false)
		this.element.removeEventListener('mouseup', this.onDocumentMouseUp, false)

		this._stopMovingCamera()
	}

	onDocumentTouchStart = ( event: TouchEvent ) => {
		if (!this.enableManualDrag) {
			return
		}
		event.preventDefault();
		event.stopPropagation();

		switch ( event.touches.length ) {
			case 1: // ROTATE
				this._startMovingCamera(event.touches[0])
				break;

			case 2: // ZOOM
				if ( this.enableManualZoom !== true ) return;

				this.appState = this.CONTROLLER_STATE.MANUAL_ZOOM;

				this.freeze = true;
				this.tmpFOV = this.object.fov;

				this.zoomP1.set( event.touches[ 0 ].pageX, event.touches[ 0 ].pageY );
				this.zoomP2.set( event.touches[ 1 ].pageX, event.touches[ 1 ].pageY );

				this.zoomStart = this.zoomCurrent = this.zoomP1.distanceTo( this.zoomP2 );


				this.element.addEventListener('touchmove', this.onDocumentTouchMove, false)
				this.element.addEventListener('touchend', this.onDocumentTouchEnd, false)

				this.fireEvent( this.CONTROLLER_EVENT.MANUAL_CONTROL + 'start' );
				this.fireEvent( this.CONTROLLER_EVENT.ZOOM_CONTROL + 'start' );

				break;

			default:
				console.warn('More than two touches!')
				break;
		}
	}

	onDocumentTouchMove = ( event: TouchEvent ) => {
		switch( event.touches.length ) {
			case 1:
				this._moveCamera(event.touches[0])
				break;

			case 2:
				this.zoomP1.set( event.touches[ 0 ].pageX, event.touches[ 0 ].pageY );
				this.zoomP2.set( event.touches[ 1 ].pageX, event.touches[ 1 ].pageY );
				break;

			default:
				console.warn('More than two touches!')
				break;
		}
	}

	onDocumentTouchEnd = () => {
		this.element.removeEventListener('touchmove', this.onDocumentTouchMove, false)
		this.element.removeEventListener('touchend', this.onDocumentTouchEnd, false)

		if ( this.appState === this.CONTROLLER_STATE.MANUAL_ROTATE ) {

			this._stopMovingCamera()

		} else if ( this.appState === this.CONTROLLER_STATE.MANUAL_ZOOM ) {

			// this.constrainObjectFOV(); // re-instate original object FOV

			this.appState = this.CONTROLLER_STATE.AUTO; // reset control state

			this.freeze = false;

			this.fireEvent( this.CONTROLLER_EVENT.MANUAL_CONTROL + 'end' );
			this.fireEvent( this.CONTROLLER_EVENT.ZOOM_CONTROL + 'end' );

		}
	}

	createQuaternion = (alpha: number, beta: number, gamma: number, screenOrientation: number) => {
		const finalQuaternion = new THREE.Quaternion()

		const deviceEuler = new THREE.Euler()

		const screenTransform = new THREE.Quaternion()

		const worldTransform = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5)) // - PI/2 around the x-axis

		deviceEuler.set(beta, alpha, -gamma, 'YXZ')

		finalQuaternion.setFromEuler(deviceEuler)

		const minusHalfAngle = -screenOrientation / 2

		screenTransform.set(0, Math.sin(minusHalfAngle), 0, Math.cos(minusHalfAngle))

		finalQuaternion.multiply(screenTransform)

		finalQuaternion.multiply(worldTransform)

		return finalQuaternion
	}

	createRotationMatrix = (alpha: number, beta: number, gamma: number, screenOrientation: number) => {
		const finalMatrix = new THREE.Matrix4()

		const deviceEuler = new THREE.Euler()
		const screenEuler = new THREE.Euler()
		const worldEuler = new THREE.Euler(-Math.PI / 2, 0, 0, 'YXZ') // - PI/2 around the x-axis

		const screenTransform = new THREE.Matrix4()

		const worldTransform = new THREE.Matrix4()
		worldTransform.makeRotationFromEuler(worldEuler)

		deviceEuler.set(beta, alpha, -gamma, 'YXZ')

		finalMatrix.identity()

		finalMatrix.makeRotationFromEuler(deviceEuler)

		screenEuler.set(0, -screenOrientation, 0, 'YXZ')

		screenTransform.identity()

		screenTransform.makeRotationFromEuler(screenEuler)

		finalMatrix.multiply(screenTransform)

		finalMatrix.multiply(worldTransform)

		return finalMatrix
	}

	updateCameraRotation = () => {
		const rotation = new THREE.Euler(0, 0, 0, 'YXZ')
		const rotQuat = new THREE.Quaternion()
		const objQuat = new THREE.Quaternion()
		objQuat.copy(this.tmpQuat)

		// Magic number that just looks and feels good. Original formula was: (expectedScreenWidth /
		// actualScreenWidth) * 0.01
		const factor = (1200 / 1428) * (-0.00135)

		const lat = (this.startY - this.currentY) * factor * this.object.fov
		const lon = (this.startX - this.currentX) * factor * this.object.fov

		const phi = THREE.Math.degToRad(lat)
		const theta = THREE.Math.degToRad(lon)

		rotQuat.set(0, Math.sin(theta / 2), 0, Math.cos(theta / 2))

		objQuat.multiply(rotQuat)

		rotQuat.set(Math.sin(phi / 2), 0, 0, Math.cos(phi / 2))

		objQuat.multiply(rotQuat)

		// Remove introduced z-axis rotation and add device's current z-axis rotation
		const tmpZ = rotation.setFromQuaternion(this.tmpQuat, 'YXZ').z
		const objZ = rotation.setFromQuaternion(objQuat, 'YXZ').z
		const realZ = rotation.setFromQuaternion(this.deviceQuat || this.tmpQuat, 'YXZ').z

		rotQuat.set(0, 0, Math.sin((realZ - tmpZ) / 2), Math.cos((realZ - tmpZ) / 2))

		this.tmpQuat.multiply(rotQuat)

		rotQuat.set(0, 0, Math.sin((realZ - objZ) / 2), Math.cos((realZ - objZ) / 2))

		objQuat.multiply(rotQuat)

		this.object.quaternion.copy(objQuat)
	}

	updateCameraFov = () => {
		const rotation = new THREE.Euler(0, 0, 0, 'YXZ')
		const rotQuat = new THREE.Quaternion()

		this.zoomCurrent = this.zoomP1.distanceTo(this.zoomP2)

		const zoomFactor = this.zoomStart / this.zoomCurrent

		const newFov = this.tmpFOV * zoomFactor
		if (DeviceOrientationController.minimumFov <= newFov && newFov <= DeviceOrientationController.maximalFov) {
			this.object.fov = newFov
			this.object.updateProjectionMatrix()
		}

		// Add device's current z-axis rotation
		if (this.deviceQuat) {
			const tmpZ = rotation.setFromQuaternion(this.tmpQuat, 'YXZ').z
			const realZ = rotation.setFromQuaternion(this.deviceQuat, 'YXZ').z

			rotQuat.set(0, 0, Math.sin((realZ - tmpZ) / 2), Math.cos((realZ - tmpZ) / 2))

			this.tmpQuat.multiply(rotQuat)

			this.object.quaternion.copy(this.tmpQuat)
		}
	}

	updateManualMove = () => {
		if (this.appState === this.CONTROLLER_STATE.MANUAL_ROTATE || this.appState === this.CONTROLLER_STATE.MANUAL_ZOOM) {
			this.updateCameraRotation()
		}

		if (this.appState === this.CONTROLLER_STATE.MANUAL_ZOOM) {
			this.updateCameraFov()
		}
	}

	updateDeviceMove = (() => {

		let alpha: number
		let beta: number
		let gamma: number
		let orient: number

		let deviceMatrix: THREE.Matrix4

		return () => {

			alpha  = THREE.Math.degToRad( this.deviceOrientation.alpha || 0 ); // Z
			beta   = THREE.Math.degToRad( this.deviceOrientation.beta  || 0 ); // X'
			gamma  = THREE.Math.degToRad( this.deviceOrientation.gamma || 0 ); // Y''
			orient = THREE.Math.degToRad( parseInt(`${this.screenOrientation}`) || 0 ); // O

			// only process non-zero 3-axis data
			if ( alpha !== 0 && beta !== 0 && gamma !== 0) {

				if ( this.useQuaternions ) {

					this.deviceQuat = this.createQuaternion( alpha, beta, gamma, orient );

				} else {

					deviceMatrix = this.createRotationMatrix( alpha, beta, gamma, orient );

					this.deviceQuat.setFromRotationMatrix( deviceMatrix );

				}

				if ( this.freeze ) return;

				//this.object.quaternion.slerp( deviceQuat, 0.07 ); // smoothing
				this.object.quaternion.copy( this.deviceQuat );

			}

		};

	})()

	update = () => {
		// this.updateDeviceMove();

		if ( this.appState !== this.CONTROLLER_STATE.AUTO ) {
			this.updateManualMove();
		}
	}

	connect = () => {
		window.addEventListener('resize', this.constrainObjectFOV, false)

		window.addEventListener('orientationchange', this.onScreenOrientationChange, false)
		window.addEventListener('deviceorientation', this.onDeviceOrientationChange, false)

		window.addEventListener('compassneedscalibration', this.onCompassNeedsCalibration, false)

		this.element.addEventListener('mousedown', this.onDocumentMouseDown, false)
		this.element.addEventListener('touchstart', this.onDocumentTouchStart, false)
		this.element.addEventListener('contextmenu', this.onDocumentContextMenu)

		this.freeze = false;
	}

	disconnect = () => {
		this.freeze = true;

		window.removeEventListener('resize', this.constrainObjectFOV, false)

		window.removeEventListener('orientationchange', this.onScreenOrientationChange, false)
		window.removeEventListener('deviceorientation', this.onDeviceOrientationChange, false)

		window.removeEventListener('compassneedscalibration', this.onCompassNeedsCalibration, false)

		this.element.removeEventListener('mousedown', this.onDocumentMouseDown, false)
		this.element.removeEventListener('touchstart', this.onDocumentTouchStart, false)
		this.element.removeEventListener('contextmenu', this.onDocumentContextMenu)
	}

}

export default DeviceOrientationController
